// FormFieldهای متصل به React Hook Form (AP-008). پیام خطای Zod کلید i18n است (مثلاً common:validation.required).
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from '@phoenix/i18n';
import { useState } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { faDigits, groupThousands, keepDigitsOnly, latinDigits } from '../format/digits';
import { normalizeMobile } from '../format/iranian';
import type { MoneyDisplayUnit } from '../format/money';
import { useDisplaySettings } from '../components/display';
import { toBusinessDate } from '../format/jalali';

interface BaseFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  labelKey: string;
  helperKey?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  fullWidth?: boolean;
}

function useFieldError(error: { message?: string } | undefined, helperKey?: string) {
  const { t } = useTranslation('common');
  if (error?.message) {
    return { error: true, helperText: t(error.message) };
  }
  return { error: false, helperText: helperKey ? t(helperKey) : undefined };
}

export type TextFieldRHFProps<T extends FieldValues> = BaseFieldProps<T> &
  Omit<TextFieldProps, 'name' | 'label' | 'value' | 'onChange' | 'onBlur' | 'error' | 'helperText'>;

export function TextFieldRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  ...rest
}: TextFieldRHFProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  return (
    <TextField
      {...rest}
      name={field.name}
      inputRef={field.ref}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      label={t(labelKey)}
      {...feedback}
    />
  );
}

export interface SelectOption {
  value: string;
  /** کلید i18n برچسب. */
  labelKey?: string;
  /** برچسب آماده (داده سرور، مثل نام استان از داده مرجع) وقتی کلید i18n ندارد. */
  label?: string;
}

export type SelectRHFProps<T extends FieldValues> = BaseFieldProps<T> & { options: readonly SelectOption[] };

export function SelectRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  options,
  ...rest
}: SelectRHFProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  return (
    <TextField
      {...rest}
      select
      name={field.name}
      inputRef={field.ref}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      label={t(labelKey)}
      {...feedback}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label ?? (option.labelKey ? t(option.labelKey) : option.value)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** مقدار: تاریخ کسب‌وکاری `yyyy-MM-dd` (میلادی، برای API) یا null؛ نمایش و ورود شمسی. */
export function JalaliDatePickerRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  required,
  disabled,
}: BaseFieldProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  const value = typeof field.value === 'string' && field.value ? parseBusinessDate(field.value) : null;
  return (
    <DatePicker
      label={t(labelKey)}
      value={value}
      disabled={disabled}
      inputRef={field.ref}
      onChange={(date: Date | null) =>
        field.onChange(date && !Number.isNaN(date.getTime()) ? toBusinessDate(date) : null)
      }
      slotProps={{
        textField: { name: field.name, required, onBlur: field.onBlur, fullWidth: true, ...feedback },
      }}
    />
  );
}

function parseBusinessDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

export type MoneyFieldRHFProps<T extends FieldValues> = BaseFieldProps<T> & {
  /** واحد ورود؛ پیش‌فرض واحد نمایش مستأجر. مقدار فرم همیشه ریال صحیح (AmountMinor) است. */
  unit?: MoneyDisplayUnit;
};

/** ورود مبلغ: نمایش با جداکننده و ارقام فارسی؛ مقدار فرم عدد صحیح ریال (ADR-013). */
export function MoneyFieldRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  unit,
  ...rest
}: MoneyFieldRHFProps<T>) {
  const { t } = useTranslation('common');
  const settings = useDisplaySettings();
  const inputUnit = unit ?? settings.currencyUnit;
  const factor = inputUnit === 'Toman' ? 10 : 1;
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  const rials = typeof field.value === 'number' ? field.value : null;
  const display = rials === null ? '' : faDigits(groupThousands(Math.trunc(rials / factor)));

  return (
    <TextField
      {...rest}
      name={field.name}
      inputRef={field.ref}
      value={display}
      onChange={(event) => {
        const digits = keepDigitsOnly(event.target.value);
        field.onChange(digits === '' ? null : Number(digits) * factor);
      }}
      onBlur={field.onBlur}
      label={t(labelKey)}
      slotProps={{
        htmlInput: { inputMode: 'numeric', dir: 'ltr', style: { textAlign: 'right' } },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {t(inputUnit === 'Toman' ? 'common:money.toman' : 'common:money.rial')}
            </InputAdornment>
          ),
        },
      }}
      {...feedback}
    />
  );
}

/** موبایل ایران: ورود ارقام فارسی یا لاتین؛ پس از خروج از فیلد به 09xxxxxxxxx نرمال می‌شود. */
export function MobileFieldRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  ...rest
}: BaseFieldProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  const [focused, setFocused] = useState(false);
  const value = typeof field.value === 'string' ? field.value : '';
  return (
    <TextField
      {...rest}
      name={field.name}
      inputRef={field.ref}
      type="tel"
      value={focused ? value : faDigits(value)}
      onFocus={() => setFocused(true)}
      onChange={(event) => field.onChange(latinDigits(event.target.value))}
      onBlur={() => {
        setFocused(false);
        const normalized = normalizeMobile(value);
        if (normalized) {
          field.onChange(normalized);
        }
        field.onBlur();
      }}
      label={t(labelKey)}
      slotProps={{ htmlInput: { inputMode: 'tel', dir: 'ltr', autoComplete: 'tel', maxLength: 16 } }}
      {...feedback}
    />
  );
}

/** کد ملی: فقط رقم، حداکثر ۱۰؛ اعتبار با zNationalId (رقم کنترل). */
export function NationalIdFieldRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  ...rest
}: BaseFieldProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  return (
    <TextField
      {...rest}
      name={field.name}
      inputRef={field.ref}
      value={typeof field.value === 'string' ? field.value : ''}
      onChange={(event) => field.onChange(keepDigitsOnly(event.target.value).slice(0, 10))}
      onBlur={field.onBlur}
      label={t(labelKey)}
      slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', maxLength: 10 } }}
      {...feedback}
    />
  );
}

/** چک‌باکس متصل به RHF (مقدار boolean). */
export function CheckboxRHF<T extends FieldValues>({
  name,
  control,
  labelKey,
  helperKey,
  disabled,
}: BaseFieldProps<T>) {
  const { t } = useTranslation('common');
  const { field, fieldState } = useController({ name, control });
  const feedback = useFieldError(fieldState.error, helperKey);
  return (
    <div>
      <FormControlLabel
        disabled={disabled}
        control={
          <Checkbox
            name={field.name}
            inputRef={field.ref}
            checked={field.value === true}
            onChange={(event) => field.onChange(event.target.checked)}
            onBlur={field.onBlur}
          />
        }
        label={t(labelKey)}
      />
      {feedback.helperText && <FormHelperText error={feedback.error}>{feedback.helperText}</FormHelperText>}
    </div>
  );
}
